import React, { useState, useEffect } from "react";
import "./InsightPanel.css";
import arrowUp from "../../../assets/images/Vector (Stroke).svg";
import arrowDown from "../../../assets/images/VectorStroke.svg";
import { IoIosArrowDropdown, IoIosArrowDropup } from "react-icons/io";
import { useSelector } from "react-redux";
import CollapsibleEvidenceRationale from "../CollapsibleEvidenceRationale";
import CollapsibleFollowUp from "../CollapsibleFollowUp";
import {
  formatAcronyms,
  formatCamelCaseWords,
  formatDateTimeEST,
} from "../../Helper/checkCondition";

import { getCallReview } from "../../../operations/services/getCallReview";

import CallReviewTable from "./CallReviewTable";
import InsightDataTable, {
  buildInsightTableRows,
  groupRows,
  evidenceToString,
  CELL,
  confidenceLabel,
  humanizeKey,
} from "./InsightDataTable";

// Field-extraction style sections that render with the generic column table
// (Field | Value | Confidence | Evidence). Agent Scoring and Follow Up Insights
// have their own shape-specific adapters (see renderAgentScoringMetrics /
// renderFollowUpTable) but reuse the same InsightDataTable component.
const TABLE_SECTION_HEADINGS = new Set([
  "Customer Profile Analysis",
  "Call Outcome",
  "Insurance / Finance Signal",
  "Consent Compliance Validation",
]);
const FOLLOW_UP_HEADING = "Follow up Insights";
const CALL_SCREEN_HEADING = "Call Screen Integrity & Communication Flow";
const GLOBAL_CORE_MEASURES_HEADING = "Global Core Measures";

// Category sections whose payload is a set of grouped objects + arrays. They
// render as per-group sub-tables via renderGroupedSection. This same renderer
// suits the other CTxx category sections; add their headings here to enable.
const GROUPED_TABLE_HEADINGS = new Set([
  "Lead Intake — Community Origin",
  "Lead Intake — Provider Origin",
  "Lead In Process — Community Origin",
  "Referral In Process — Provider Origin",
  "Clinical Assessment Encounter",
  "Level of Care Discussion",
  "Enrollment & Admissions Encounter",
  "Post-Discharge Follow-Up",
  "General Inquiry & Support",
  "Uncategorized Call Triage Gate",
  "Short Disrupted Calls",
]);

const InsightPanel = ({
  memberId,
  memberDetails,
  section,
  callId,
  style,
  forceExpand = false,
  onReadyChange,
  onCallReviewData,
  // Lifts the call's review context (category + IDs) up to the Details page so
  // it can offer a feedback modal for non-community (no-scoring) call types.
  onReviewContext,
  // Incrementing signal from the Details page's top-right button; for
  // community-origin calls it opens the existing scoring (Edit Scores) modal.
  openScoringSignal,
}) => {
  const normalizeKey = (key) => {
    return key
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");
  };

  // Scalar "no data" test for page-view N/A suppression (Insights Details
  // simplification). Excludes "no"/"none" — those are legitimate findings.
  const NA_SCALAR_TOKENS = new Set([
    "",
    "n/a",
    "na",
    "null",
    "undefined",
    "not mentioned",
    "not applicable",
    "not provided",
  ]);
  const isNaScalar = (v) => {
    if (v === null || v === undefined) return true;
    if (typeof v === "object") return false;
    return NA_SCALAR_TOKENS.has(String(v).trim().toLowerCase());
  };

  // Plain binary-negative answers. Per the design review these are hidden in
  // the page view to cut noise (e.g. "Follow-up Sent: No"); the PDF keeps
  // everything. Kept tight to "no"/"false" so meaningful words like "None" are
  // not dropped. Binary FLAG tables are exempt — they render every flag as a
  // compact chip regardless (see renderFlagGrid).
  const NEGATIVE_SCALAR_TOKENS = new Set(["no", "false"]);
  const isNegativeScalar = (v) => {
    if (v === null || v === undefined) return false;
    if (typeof v === "object") return false;
    return NEGATIVE_SCALAR_TOKENS.has(String(v).trim().toLowerCase());
  };
  // A scalar the page view should suppress: empty / N-A OR a plain "No".
  const isSuppressedScalar = (v) => isNaScalar(v) || isNegativeScalar(v);

  console.log(section, "section insgigt");

  const [callReviewData, setCallReviewData] = useState(null);
  // Starts true (not false) so any consumer reading readiness on the very
  // first render (before the fetch effect below has run) correctly treats
  // this panel as "not settled yet" rather than racily reporting ready.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [is404, setIs404] = useState(false);
  const [isEvalScoreExpanded, setIsEvalScoreExpanded] = useState(true);

  const loginData = useSelector((state) => state.login.login.data);
  const userRole = loginData?.data[0]?.Role;
  console.log(userRole, "userROle");

  const fetchCallReview = async () => {
    if (!callId) {
      console.warn("⚠️ callId is missing, skipping API call");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setIs404(false);

    try {
      const data = await getCallReview(callId);
      console.log(data, "callReview");
      setCallReviewData(data);
    } catch (err) {
      console.error("❌ FE Error:", err.message);
      if (err.status === 404) {
        setIs404(true);
        setCallReviewData(null);
      } else {
        setError(err.message || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCallReview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId]);

  const hasFollowUpFields = (obj) => {
    if (!obj || typeof obj !== "object") return false;
    const followUpKeys = [
      "follow_up_commitment",
      "follow_up_details",
      "follow_up_evidence",
      "follow_up_confidence_score",
      "follow_up_scheduled",
      "follow_up_date",
      "follow_up_purpose",
    ];
    return Object.keys(obj).some((key) =>
      followUpKeys.includes(normalizeKey(key)),
    );
  };

  const extractFollowUpFields = (obj) => {
    const extracted = {
      commitment: null,
      details: null,
      evidence: null,
      confidence: null,
      scheduled: null,
      date: null,
      purpose: null,
    };

    if (!obj || typeof obj !== "object") return extracted;

    Object.entries(obj).forEach(([key, value]) => {
      const normalized = normalizeKey(key);
      if (normalized === "follow_up_commitment") extracted.commitment = value;
      else if (normalized === "follow_up_details") extracted.details = value;
      else if (normalized === "follow_up_evidence") extracted.evidence = value;
      else if (normalized === "follow_up_confidence_score")
        extracted.confidence = value;
      else if (normalized === "follow_up_scheduled")
        extracted.scheduled = value;
      else if (normalized === "follow_up_date") extracted.date = value;
      else if (normalized === "follow_up_purpose") extracted.purpose = value;
    });

    return extracted;
  };

  const removeFollowUpFields = (obj) => {
    if (!obj || typeof obj !== "object") return obj;
    const followUpKeys = [
      "follow_up_commitment",
      "follow_up_details",
      "follow_up_evidence",
      "follow_up_confidence_score",
    ];
    const filtered = {};
    Object.entries(obj).forEach(([key, value]) => {
      if (!followUpKeys.includes(normalizeKey(key))) {
        filtered[key] = value;
      }
    });
    return filtered;
  };

  const [expandedSections, setExpandedSections] = useState([0, 1, 2, 3]);
  const [activeControl, setActiveControl] = useState("collapse");
  const [mappingData, setMappingData] = useState({});
  const [callTimings, setCallTimings] = useState();
  const filteredData = useSelector(
    (state) => state.operationHome.table.filteredData,
  );

  console.log(filteredData);
  const [expandSectionsIndex, setExpandSectionsIndex] = useState(4);

  // --- FEEDBACK STATES ---
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [selectedObservation, setSelectedObservation] = useState(2);
  const [observationText, setObservationText] = useState("");

  // List of L1_promptIds that should have heading-based summary
  const executiveSummaryL1Ids = [
    "P35",
    "P36",
    "P37",
    "P38",
    "P39",
    "P40",
    "P41",
    "P42",
    "P43",
    "P44",
    "P45",
    "P46",
    "P47",
    "P48",
    "P49",
    "P50",
    "P51",
    "P52",
    "P54",
  ];

  // ✅ Function to clean Beginning/Middle/End formatting for P13
  const cleanBeginningMiddleEnd = (text, L1_promptId) => {
    if (L1_promptId !== "P13" || typeof text !== "string") return text;

    // Remove "Beginning:", "Middle:", and "End:" labels but keep the content
    return text
      .replace(/Beginning:\s*/gi, "")
      .replace(/Middle:\s*/gi, "")
      .replace(/End:\s*/gi, "")
      .trim();
  };
  // ✅ Function to transform PromptResponse and extract summary
  const transformAndExtractSummary = (response, heading, L1_promptId) => {
    if (!response || typeof response !== "string")
      return { transformedResponse: response, summary: null };

    // Only attempt JSON parse when the string actually looks like JSON
    const trimmed = response.trim();
    if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
      return { transformedResponse: response, summary: null };
    }

    try {
      const parsed = JSON.parse(trimmed);

      // Check if this L1_promptId should be transformed
      if (
        executiveSummaryL1Ids.includes(L1_promptId) &&
        heading &&
        parsed.executive_summary
      ) {
        // Create new key name based on heading
        const newKey = heading.toLowerCase().replace(/\s+/g, "_") + "_summary";

        // Create new object with the transformed key
        const transformed = {
          ...parsed,
          [newKey]: parsed.executive_summary,
        };

        // Remove the old executive_summary key
        delete transformed.executive_summary;

        return {
          transformedResponse: JSON.stringify(transformed),
          summary: {
            key: newKey,
            value: parsed.executive_summary,
            displayLabel: heading + " Summary",
          },
        };
      }

      return { transformedResponse: response, summary: null };
    } catch (err) {
      console.warn("Error transforming PromptResponse:", err);
      return { transformedResponse: response, summary: null };
    }
  };

  const formatConfidenceValue = (key, value) => {
    if (!key) return value;

    const lowerKey = key.toLowerCase();

    if (lowerKey.includes("confidence")) {
      const num = Number(value);

      if (num === 5) return "High";
      if (num === 3) return "Medium";
      if (num === 1) return "Low";
    }

    return value;
  };

  // ✅ Function to replace confidence scores with text labels
  const replaceConfidenceScore = (text) => {
    if (!text || typeof text !== "string") return text;

    // Replace confidence score patterns
    // Matches: "Confidence Score - 1", "Confidence Score: 1", "confidence score 1", etc.
    return text.replace(
      /(Confidence Score|confidence score)[\s\-:]*(\d+)/gi,
      (match, prefix, score) => {
        const scoreNum = parseInt(score, 10);
        let confidenceText = "";

        switch (scoreNum) {
          case 1:
            confidenceText = "Low";
            break;
          case 3:
            confidenceText = "Medium";
            break;
          case 5:
            confidenceText = "High";
            break;
          default:
            confidenceText = score; // keep original if not 1,3,5
        }

        return `Confidence Score - ${confidenceText}`;
      },
    );
  };

  // ✅ Parse mapping data when component mounts or memberDetails changes
  useEffect(() => {
    if (memberDetails?.callDetails && callId) {
      const currentCallDetail = memberDetails.callDetails.find(
        (item) => item.CallId === callId,
      );

      const callTiminsInfo = filteredData.find(
        (item) => item.callId === callId,
      );

      if (callTiminsInfo) {
        setCallTimings(callTiminsInfo);
      }

      if (currentCallDetail?.Mapping) {
        try {
          const parsedMapping =
            typeof currentCallDetail.Mapping === "string"
              ? JSON.parse(currentCallDetail.Mapping)
              : currentCallDetail.Mapping;
          setMappingData(parsedMapping);
        } catch (err) {
          setMappingData({});
        }
      }
    }
  }, [memberDetails, callId, filteredData]);

  // ✅ Function to replace mapped values in any string
  const replaceMappedValues = (text) => {
    if (
      !text ||
      typeof text !== "string" ||
      Object.keys(mappingData).length === 0
    ) {
      return replaceConfidenceScore(text); // Apply confidence score replacement even without mapping
    }

    let result = text;

    // Pattern to match things like PERSON_76, LOCATION_12, etc. (with underscore)
    const underscorePattern = /\b([A-Z]+(?:_[A-Z]+)?)_(\d+)\b/gi;

    // Pattern to match things like PERSON 76, LOCATION 12, etc. (with space)
    const spacePattern = /\b([A-Z]+(?:_[A-Z]+)?)\s+(\d+)\b/gi;

    // First replace underscore format
    result = result.replace(underscorePattern, (match, category, id) => {
      const upperCategory = category.toUpperCase();
      const key = `${upperCategory}_${id}`;

      for (const [mappingCategory, values] of Object.entries(mappingData)) {
        if (values && typeof values === "object" && values[key]) {
          return values[key];
        }
      }

      if (mappingData[upperCategory] && mappingData[upperCategory][key]) {
        return mappingData[upperCategory][key];
      }

      return match;
    });

    // Then replace space format
    result = result.replace(spacePattern, (match, category, id) => {
      const upperCategory = category.toUpperCase();
      const key = `${upperCategory}_${id}`;

      for (const [mappingCategory, values] of Object.entries(mappingData)) {
        if (values && typeof values === "object" && values[key]) {
          return values[key];
        }
      }

      if (mappingData[upperCategory] && mappingData[upperCategory][key]) {
        return mappingData[upperCategory][key];
      }

      return match;
    });

    // Finally, replace confidence scores
    let formatted = replaceConfidenceScore(result);

    // Fix CamelCase spacing
    formatted = formatCamelCaseWords(formatted);

    formatted = formatAcronyms(formatted);

    return formatted;
  };

  // PDF export renders this same component with forceExpand=true and
  // screenshots the DOM (see InsightPDFDownload.js). The Insights Details
  // simplification hides/relocates sections in the on-screen page view ONLY —
  // the PDF must still contain every section. So all page-view suppression and
  // relocation below is gated on `!isPdf`; the PDF path ignores the
  // pageVisible/pageHeading flags the API attaches.
  const isPdf = forceExpand;

  // ✅ Group items by heading (preserved from old code)
  const groupedSections = {};
  const sectionSummaries = {}; // Store extracted summaries for each section

  (section?.items || [])
    .filter((sec) => sec.Render !== "N")
    // Page view honors the API's pageVisible flag; PDF keeps everything.
    .filter((sec) => isPdf || sec.pageVisible !== false)
    .forEach((sec) => {
      // In the page view a relocated field (e.g. Follow Up Required moved out
      // of the removed Purpose section) groups under pageHeading; the PDF keeps
      // the original heading so its layout is unchanged.
      const effectiveHeading =
        (!isPdf && sec.pageHeading) || sec.heading || "Untitled";
      const headingKey = effectiveHeading;
      if (!groupedSections[headingKey]) {
        groupedSections[headingKey] = {
          heading: headingKey,
          PromptResponses: [],
        };
        sectionSummaries[headingKey] = null;
      }

      const mappedSec = { ...sec };

      // Transform the PromptResponse and extract summary if needed
      if (
        mappedSec.PromptResponse &&
        typeof mappedSec.PromptResponse === "string"
      ) {
        const { transformedResponse, summary } = transformAndExtractSummary(
          mappedSec.PromptResponse,
          mappedSec.heading,
          mappedSec.L1_promptId,
        );

        // Store summary for this section if found
        if (summary) {
          sectionSummaries[headingKey] = summary;
        }

        // Apply value mapping to transformed response
        mappedSec.PromptResponse = replaceMappedValues(transformedResponse);
      }

      groupedSections[headingKey].PromptResponses.push(mappedSec);
    });

  // The standalone "Global Core Measures" section is removed entirely (page AND
  // PDF): its measures are now surfaced with full detail + average in the
  // Magic Health Evaluation Score Summary at the top of Conversation Insights,
  // so keeping the section here would just duplicate that data.
  delete groupedSections[GLOBAL_CORE_MEASURES_HEADING];

  const sortedSections = Object.values(groupedSections);

  // ✅ Auto-expand for PDF export
  useEffect(() => {
    if (forceExpand && section?.items) {
      setExpandedSections(sortedSections.map((_, idx) => idx));
    }
  }, [forceExpand, section, sortedSections]);

  const extractAllScores = () => {
    if (!section || !section.items || section.items.length === 0) return [];

    const allScores = [];

    const globalCoreMeasuresItem = section.items.find(
      (item) => item.L2_promptId === "155",
    );

    if (!globalCoreMeasuresItem) {
      return [];
    }

    if (!globalCoreMeasuresItem.PromptResponse) {
      return [];
    }

    const response = globalCoreMeasuresItem.PromptResponse;

    try {
      const parsed =
        typeof response === "string" ? JSON.parse(response) : response;

      if (
        parsed &&
        parsed.global_core_measures &&
        Array.isArray(parsed.global_core_measures)
      ) {
        parsed.global_core_measures.forEach((measure) => {
          if (
            measure.score !== undefined &&
            measure.score !== null &&
            !isNaN(measure.score)
          ) {
            const mappedMeasure = replaceMappedValues(measure.measure || "");

            // Convert confidence score if present
            let confidence = measure.confidence;
            if (confidence === 1) confidence = "Low";
            else if (confidence === 3) confidence = "Medium";
            else if (confidence === 5) confidence = "High";

            allScores.push({
              category: "Global Core Measures",
              subCategory: `${measure.id}: ${mappedMeasure}`,
              score: measure.score,
              rawScores: [measure.score],
              promptId: 155,
              measureId: measure.id,
              evidence: measure.evidence,
              rationale: measure.rationale,
              confidence: confidence,
              itemData: measure,
            });
          }
        });
      }
    } catch (err) {
      return [];
    }

    return allScores;
  };

  const extractFilteredScores = () => {
    const allScores = extractAllScores();

    const allowedMeasures = [
      "sensitivity to needs",
      "cultural and linguistic awareness",
      "closing and relationship continuity",
    ];

    return allScores.filter((scoreItem) => {
      const subCategoryLower = scoreItem.subCategory.toLowerCase();
      return allowedMeasures.some((allowed) =>
        subCategoryLower.includes(allowed),
      );
    });
  };

  const renderAgentScoringMetrics = (promptResponseJson) => {
    try {
      const parsed =
        typeof promptResponseJson === "string"
          ? JSON.parse(promptResponseJson)
          : promptResponseJson;

      if (!parsed.communication_quality) {
        return <div>No communication quality data available</div>;
      }

      const requiredMetrics = [
        "sensitivity_to_needs",
        "cultural_and_linguistic_awareness",
        "closing_and_relationship_continuity",
      ];

      const metrics = [];

      requiredMetrics.forEach((metricKey) => {
        const metricData = parsed.communication_quality[metricKey];
        if (metricData) {
          metrics.push({
            displayName: formatLabel(metricKey),
            score: metricData.score,
            evidence: metricData.evidence,
            rationale: metricData.rationale,
          });
        }
      });

      if (metrics.length === 0) {
        return <div>No metrics data available</div>;
      }

      // Same metrics as before (Agent Scoring is "KEEP" — only its display
      // changes), now rendered via the reusable table as
      // Metric | Score | Evidence | Rationale.
      const rows = metrics.map((m) => ({
        metric: m.displayName,
        score: m.score !== undefined && m.score !== null ? String(m.score) : "",
        evidence: replaceMappedValues(m.evidence || ""),
        rationale: replaceMappedValues(m.rationale || ""),
      }));

      const columns = [
        {
          header: "Metric",
          accessor: "metric",
          cellStyle: CELL.field,
          width: "18%",
        },
        {
          header: "Score (out of 5)",
          accessor: "score",
          cellStyle: CELL.meta,
          width: "10%",
        },
        {
          header: "Evidence",
          accessor: "evidence",
          cellStyle: CELL.evidence,
          width: "36%",
        },
        { header: "Rationale", accessor: "rationale", width: "36%" },
      ];

      return <InsightDataTable rows={rows} columns={columns} />;
    } catch (err) {
      console.error("Error rendering agent scoring metrics:", err);
      return <div>Error rendering metrics data</div>;
    }
  };

  const renderScoreTable = (useFilteredScores = false) => {
    const extractedScores = useFilteredScores
      ? extractFilteredScores()
      : extractAllScores();

    const sanitizedScores = Array.isArray(extractedScores)
      ? extractedScores.map((s) => {
          const { evidence, rationale, ...keep } = s;
          return keep;
        })
      : [];

    if (!Array.isArray(sanitizedScores) || sanitizedScores.length === 0) {
      const hasPrompt155 = section?.items?.some(
        (item) => item.L2_promptId === "155",
      );

      return (
        <div
          style={{
            fontStyle: "italic",
            color: "#666",
            padding: "20px",
            textAlign: "center",
            backgroundColor: "#f9f9f9",
            borderRadius: "4px",
            border: "1px solid #e0e0e0",
          }}
        >
          {!hasPrompt155 ? (
            <>
              <p style={{ margin: "0 0 8px 0", fontWeight: "500" }}>
                ⓘ No Core Measures Available
              </p>
              <p style={{ margin: "0", fontSize: "0.9em" }}>
                This call does not contain any core measures evaluation data.
              </p>
            </>
          ) : (
            <>
              <p style={{ margin: "0 0 8px 0", fontWeight: "500" }}>
                ⓘ No Scores to Display
              </p>
              <p style={{ margin: "0", fontSize: "0.9em" }}>
                The core measures data is available but contains no scores.
              </p>
            </>
          )}
        </div>
      );
    }

    const categorized = {};
    sanitizedScores.forEach((scoreItem) => {
      const category = scoreItem.category;
      if (!categorized[category]) categorized[category] = [];
      categorized[category].push(scoreItem);
    });

    const categoryAverages = {};
    let allScoresSum = 0;
    let allScoresCount = 0;

    Object.entries(categorized).forEach(([category, scores]) => {
      const categoryTotal = scores.reduce((sum, item) => sum + item.score, 0);
      const categoryAverage =
        scores.length > 0 ? categoryTotal / scores.length : 0;
      categoryAverages[category] = categoryAverage;
      allScoresSum += categoryTotal;
      allScoresCount += scores.length;
    });

    const overallAverage =
      allScoresCount > 0 ? allScoresSum / allScoresCount : 0;

    return (
      <div className="call-review-container">
        <div className="table-wrapper">
          <table className="call-review-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Sub-Category</th>
                <th>Score (out of 5)</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(categorized).map(([category, scores]) => {
                const totalRowsForCategory =
                  scores.reduce((sum, scoreItem) => {
                    const hasDetail =
                      (scoreItem.evidence &&
                        String(scoreItem.evidence).trim()) ||
                      (scoreItem.rationale &&
                        String(scoreItem.rationale).trim());
                    return sum + 1 + (hasDetail ? 1 : 0);
                  }, 0) + 1;

                return (
                  <React.Fragment key={category}>
                    {scores.map((scoreItem, idx) => {
                      const hasDetail =
                        (scoreItem.evidence &&
                          String(scoreItem.evidence).trim()) ||
                        (scoreItem.rationale &&
                          String(scoreItem.rationale).trim());
                      return (
                        <React.Fragment key={`${scoreItem.promptId}-${idx}`}>
                          <tr>
                            {idx === 0 && (
                              <td
                                rowSpan={totalRowsForCategory}
                                style={{ verticalAlign: "top" }}
                              >
                                {category}
                              </td>
                            )}
                            <td>{scoreItem.subCategory}</td>
                            <td>{scoreItem.score.toFixed(2)}</td>
                          </tr>
                          {hasDetail && (
                            <tr>
                              <td
                                colSpan="3"
                                style={{ padding: 0, border: "none" }}
                              >
                                <CollapsibleEvidenceRationale
                                  evidence={replaceMappedValues(
                                    scoreItem.evidence || "",
                                  )}
                                  rationale={replaceMappedValues(
                                    scoreItem.rationale || "",
                                  )}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    <tr className="total-row">
                      <td>Average Score – {category}</td>
                      <td>{categoryAverages[category].toFixed(1)}</td>
                    </tr>
                  </React.Fragment>
                );
              })}
              <tr className="total-row">
                <td colSpan="2">Overall Quality Score</td>
                <td>{overallAverage.toFixed(1)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ─── Core Measures summary (top of Conversation Insights) ───
  // Replaces the old average-only score table. The standalone "Global Core
  // Measures" section is removed, so this summary now carries the FULL detail
  // that section used to show (Measure | Score | Confidence | Evidence |
  // Rationale) from the same prompt-155 payload, plus the overall average as a
  // highlighted final row — one place for this data instead of two.
  const renderCoreMeasuresSummaryTable = () => {
    const scores = extractAllScores();

    // Reuse the existing empty-state messaging when there are no scores.
    if (!Array.isArray(scores) || scores.length === 0) {
      return renderScoreTable();
    }

    const rows = scores.map((s) => ({
      measure: replaceMappedValues(s.subCategory || ""),
      score:
        s.score !== undefined && s.score !== null ? String(s.score) : "",
      confidence: s.confidence || "",
      evidence: replaceMappedValues(s.evidence || ""),
      rationale: replaceMappedValues(s.rationale || ""),
    }));

    const overallAverage =
      scores.reduce((sum, s) => sum + (Number(s.score) || 0), 0) /
      scores.length;

    rows.push({
      measure: "Overall Quality Score",
      score: overallAverage.toFixed(1),
      confidence: "",
      evidence: "",
      rationale: "",
      __rowStyle: { backgroundColor: "#d6e0e6", fontWeight: 700 },
    });

    const columns = [
      {
        header: "Measure",
        accessor: "measure",
        cellStyle: CELL.field,
        width: "20%",
      },
      {
        header: "Score (out of 5)",
        accessor: "score",
        cellStyle: CELL.meta,
        width: "12%",
      },
      {
        header: "Confidence",
        accessor: "confidence",
        cellStyle: CELL.meta,
        width: "10%",
      },
      {
        header: "Evidence",
        accessor: "evidence",
        cellStyle: CELL.evidence,
        width: "29%",
      },
      { header: "Rationale", accessor: "rationale", width: "29%" },
    ];

    // "scoring" theme keeps this grouped visually with the MN Call Review table.
    return (
      <InsightDataTable rows={rows} columns={columns} variant="scoring" />
    );
  };

  // Column config for a Field | Value table. Confidence & Evidence columns are
  // appended only when at least one row actually carries evidence; confidence
  // shows only in rows that have evidence (per the design notes).
  const fieldValueColumns = (rows) => {
    const anyEvidence = rows.some(
      (r) => r.evidence && String(r.evidence).trim(),
    );
    if (!anyEvidence) {
      return [
        {
          header: "Field",
          accessor: "field",
          cellStyle: CELL.field,
          width: "30%",
        },
        { header: "Value", accessor: "value", width: "70%" },
      ];
    }
    return [
      {
        header: "Field",
        accessor: "field",
        cellStyle: CELL.field,
        width: "18%",
      },
      { header: "Value", accessor: "value", width: "27%" },
      {
        header: "Confidence",
        accessor: (r) => (r.evidence ? confidenceLabel(r.confidence) : ""),
        cellStyle: CELL.meta,
        width: "13%",
      },
      {
        header: "Evidence",
        accessor: (r) => (r.evidence ? r.evidence : ""),
        cellStyle: CELL.evidence,
        width: "42%",
      },
    ];
  };

  // ─── Reusable column-based (table) display for field-extraction sections ───
  // Parses a section's JSON, flattens it to rows via the shared normalizer,
  // applies value mapping + page-view N/A filtering, then renders the reusable
  // InsightDataTable. Any section listed in TABLE_SECTION_HEADINGS routes here,
  // so onboarding a field-style section is a one-line change.
  const renderDataTable = (promptResponseJson) => {
    let parsed;
    try {
      parsed =
        typeof promptResponseJson === "string"
          ? JSON.parse(promptResponseJson)
          : promptResponseJson;
    } catch {
      return null;
    }

    const rows = buildInsightTableRows(parsed)
      // Page view drops N/A fields; the PDF keeps everything.
      .filter((r) => isPdf || !isSuppressedScalar(r.value))
      .map((r) => ({
        field: r.field,
        value: replaceMappedValues(r.value),
        confidence: r.confidence,
        evidence: r.evidence ? replaceMappedValues(String(r.evidence)) : "",
      }));

    if (rows.length === 0) return null;
    return <InsightDataTable rows={rows} columns={fieldValueColumns(rows)} />;
  };

  // ─── Follow Up Insights adapter ───
  // The follow-up payload is a single task record: flat fields, a nested
  // dependency_detected group, and ONE overall evidence/confidence.
  //
  // Per the design review this table was mostly empty (10 rows × 4 cols with
  // only field+value populated). So the headline "Follow Up Required" flag —
  // together with the overall evidence and confidence — is lifted OUT into a
  // compact callout above, and the remaining fields render as a clean,
  // well-spaced TWO-column Field | Value table (no sparse evidence/confidence
  // columns).
  const renderFollowUpTable = (promptResponseJson) => {
    let parsed;
    try {
      parsed =
        typeof promptResponseJson === "string"
          ? JSON.parse(promptResponseJson)
          : promptResponseJson;
    } catch {
      return null;
    }
    if (!parsed || typeof parsed !== "object") return null;

    const EXCLUDE = new Set([
      "module_name",
      "module_ref",
      "logic_validation",
      "evidence",
      "confidence",
    ]);

    let requiredValue = "";
    let rows = [];
    Object.entries(parsed).forEach(([key, value]) => {
      if (EXCLUDE.has(key) || value === null || value === undefined) return;
      // The headline flag is surfaced in the callout, not the table.
      if (/follow.?up.?required/i.test(key) && typeof value !== "object") {
        requiredValue = replaceMappedValues(String(value));
        return;
      }
      if (Array.isArray(value)) {
        const joined = value
          .filter((v) => v != null && String(v).trim() !== "")
          .join(", ");
        if (joined) rows.push({ field: humanizeKey(key), value: joined });
      } else if (typeof value === "object") {
        // nested group (e.g. dependency_detected) → one row per sub-field
        Object.entries(value).forEach(([sk, sv]) => {
          if (sv !== null && sv !== undefined)
            rows.push({ field: humanizeKey(sk), value: String(sv) });
        });
      } else {
        rows.push({ field: humanizeKey(key), value: String(value) });
      }
    });

    rows = rows
      .filter((r) => isPdf || !isSuppressedScalar(r.value))
      .map((r) => ({ ...r, value: replaceMappedValues(r.value) }));

    const overallEvidence = parsed.evidence?.evidence_text
      ? replaceMappedValues(String(parsed.evidence.evidence_text))
      : "";
    const overallConfidence = confidenceLabel(parsed.confidence);

    // Callout: the headline flag + its evidence/confidence, out of the table.
    const callout =
      requiredValue || overallEvidence ? (
        <div
          style={{
            marginBottom: rows.length ? "12px" : 0,
            padding: "8px 12px",
            background: "#eef4f1",
            borderLeft: "3px solid #2e7d5b",
            borderRadius: "6px",
          }}
        >
          {requiredValue ? (
            <div
              style={{ fontWeight: 700, color: "#1b4d3a", fontSize: "14px" }}
            >
              Follow Up Required: {requiredValue}
              {overallConfidence ? (
                <span style={{ fontWeight: 500, color: "#5b6b63" }}>
                  {"  ·  Confidence: "}
                  {overallConfidence}
                </span>
              ) : null}
            </div>
          ) : null}
          {overallEvidence ? (
            <div
              style={{
                marginTop: "4px",
                color: "#555",
                fontStyle: "italic",
                fontSize: "13px",
              }}
            >
              “{overallEvidence}”
            </div>
          ) : null}
        </div>
      ) : null;

    // Remaining fields as a spacious two-column table.
    const twoCol = [
      {
        header: "Field",
        accessor: "field",
        cellStyle: CELL.field,
        width: "34%",
      },
      { header: "Value", accessor: "value", width: "66%" },
    ];

    const table =
      rows.length > 0 ? (
        <InsightDataTable rows={rows} columns={twoCol} />
      ) : null;

    if (!callout && !table) return null;
    return (
      <div>
        {callout}
        {table}
      </div>
    );
  };

  // Section-level renderer for Follow Up Insights. When the JSON "Follow Up
  // Detail" prompt exists it already contains the Follow Up Required field, so
  // we render just that table and drop the separately-relocated "Follow Up
  // Required: Yes" prompt to avoid the duplicate block. If there is no JSON
  // detail (e.g. it was N/A), we fall back to the plain relocated rows.
  const renderFollowUpSection = (secs) => {
    const list = secs || [];
    const detail = list.find(
      (s) =>
        typeof s.PromptResponse === "string" &&
        s.PromptResponse.trim().startsWith("{"),
    );
    if (detail) {
      const table = renderFollowUpTable(detail.PromptResponse);
      if (table) return table;
    }

    // No detail table — surface the remaining plain rows as a Field | Value table.
    const rows = list
      .filter(
        (s) =>
          typeof s.PromptResponse === "string" &&
          !s.PromptResponse.trim().startsWith("{"),
      )
      .map((s) => ({
        field: replaceMappedValues(s.subHeading || ""),
        value: s.PromptResponse,
      }))
      .filter((r) => isPdf || !isSuppressedScalar(r.value));

    if (rows.length === 0) return null;
    return <InsightDataTable rows={rows} columns={fieldValueColumns(rows)} />;
  };

  // ─── Binary flag groups → "Present / Not Present" pivot ───
  // A group whose values are ALL Yes/No (e.g. Trigger Flags) wastes a lot of
  // vertical space as a tall row-per-flag table. Per the reviewer's mock we
  // pivot it into a compact two-column table: flags that are true are listed
  // under "Present", the rest under "Not Present". All flags are kept (this is
  // exempt from the page-view "No"-hiding, by design).
  const BINARY_TRUE = new Set(["yes", "true"]);
  const BINARY_FALSE = new Set(["no", "false"]);
  const isBinaryFlagObject = (obj) => {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return false;
    const entries = Object.entries(obj).filter(
      ([, v]) => v !== null && v !== undefined,
    );
    if (entries.length < 3) return false; // not worth pivoting below 3 flags
    return entries.every(([, v]) => {
      if (typeof v === "boolean") return true;
      if (typeof v === "object") return false;
      const s = String(v).trim().toLowerCase();
      return BINARY_TRUE.has(s) || BINARY_FALSE.has(s);
    });
  };
  const renderFlagPivot = (title, obj) => {
    const present = [];
    const notPresent = [];
    Object.entries(obj).forEach(([k, v]) => {
      if (v === null || v === undefined) return;
      const s =
        typeof v === "boolean"
          ? v
            ? "yes"
            : "no"
          : String(v).trim().toLowerCase();
      (BINARY_TRUE.has(s) ? present : notPresent).push(humanizeKey(k));
    });
    if (present.length === 0 && notPresent.length === 0) return null;

    // Stacked list inside a cell, one flag per line.
    const listCell = (items) =>
      items.length ? (
        <div>
          {items.map((x, i) => (
            <div key={i} style={{ padding: "1px 0" }}>
              {x}
            </div>
          ))}
        </div>
      ) : (
        <span style={{ color: "#9aa2ab" }}>—</span>
      );

    const rows = [
      {
        present: listCell(present),
        notPresent: listCell(notPresent),
      },
    ];
    const columns = [
      {
        header: "Present",
        accessor: "present",
        width: "50%",
        cellStyle: { color: "#1b4d3a", fontWeight: 600 },
      },
      {
        header: "Not Present",
        accessor: "notPresent",
        width: "50%",
        cellStyle: { color: "#6b7280" },
      },
    ];

    return (
      <div style={{ marginBottom: "16px" }}>
        {title ? <h6 className="headings">{title}</h6> : null}
        <InsightDataTable rows={rows} columns={columns} />
      </div>
    );
  };

  // ─── Grouped category sections (e.g. Lead Intake) ───
  // Their payload is a mix of: top-level scalars (executive_summary, notes),
  // grouped objects using the field/field_evidence/field_confidence sibling
  // pattern, and arrays of uniform objects (action_candidates, signals, ...).
  // Each group renders as its own titled sub-table so the section stays
  // scannable. This renderer also fits the other CTxx category sections.
  const renderArrayTable = (title, arr) => {
    // Union of item keys, first-seen order → columns.
    const keys = [];
    arr.forEach((o) => {
      if (o && typeof o === "object")
        Object.keys(o).forEach((k) => {
          if (!keys.includes(k)) keys.push(k);
        });
    });
    if (keys.length === 0) return null;

    const rows = arr.map((item) => {
      const row = {};
      keys.forEach((k) => {
        let v = item ? item[k] : undefined;
        if (v === null || v === undefined) {
          row[k] = "";
          return;
        }
        if (Array.isArray(v)) v = v.join(", ");
        else if (typeof v === "object") v = evidenceToString(v);
        let s = String(v);
        if (/confidence/i.test(k) && !/evidence/i.test(k))
          s = confidenceLabel(v);
        row[k] = replaceMappedValues(s);
      });
      return row;
    });

    // Page view drops rows whose every cell is empty or a plain "No" — a row
    // that carries no real signal. The PDF keeps all rows.
    const visibleRows = isPdf
      ? rows
      : rows.filter(
          (r) => !keys.every((k) => r[k] === "" || isSuppressedScalar(r[k])),
        );
    if (visibleRows.length === 0) return null;

    // Relative widths (fixed layout scales them to 100%): text-heavy columns
    // get more room; short meta columns get less.
    const widthFor = (k, idx) => {
      if (/(evidence|description|rationale|details|notes|summary)/i.test(k))
        return "26%";
      if (/(confidence|score|segment|readiness)/i.test(k)) return "9%";
      return idx === 0 ? "16%" : "12%";
    };

    const columns = keys.map((k, idx) => ({
      header: humanizeKey(k),
      accessor: k,
      width: widthFor(k, idx),
      cellStyle: /evidence/i.test(k)
        ? CELL.evidence
        : /(confidence|score|segment|readiness)/i.test(k)
          ? CELL.meta
          : idx === 0
            ? CELL.field
            : undefined,
    }));

    return (
      <div>
        <h6 className="headings">{title}</h6>
        <InsightDataTable rows={visibleRows} columns={columns} />
      </div>
    );
  };

  const renderGroupedSection = (promptResponseJson) => {
    let parsed;
    try {
      parsed =
        typeof promptResponseJson === "string"
          ? JSON.parse(promptResponseJson)
          : promptResponseJson;
    } catch {
      return null;
    }
    if (!parsed || typeof parsed !== "object") return null;

    // Each block is { content, full }. `full` blocks (array tables, flag grids,
    // the executive summary) span the whole width; two-column Field|Value
    // sub-tables are `full: false` so pairs of them sit side-by-side (see the
    // responsive grid below) instead of each hogging a full row of white space.
    const blocks = [];
    const topScalars = [];

    Object.entries(parsed).forEach(([key, value]) => {
      if (value === null || value === undefined) return;

      if (Array.isArray(value)) {
        if (value.length === 0) return;
        if (typeof value[0] === "object") {
          const tbl = renderArrayTable(humanizeKey(key), value);
          if (tbl) blocks.push({ content: tbl, full: true });
        } else {
          topScalars.push({ field: humanizeKey(key), value: value.join(", ") });
        }
        return;
      }

      if (typeof value === "object") {
        // A group of pure Yes/No flags → Present/Not Present pivot, not a tall
        // row-per-flag table.
        if (isBinaryFlagObject(value)) {
          const grid = renderFlagPivot(humanizeKey(key), value);
          if (grid) {
            blocks.push({ content: grid, full: true });
            return;
          }
        }

        const rows = groupRows(value)
          .filter((r) => isPdf || !isSuppressedScalar(r.value))
          .map((r) => ({
            field: r.field,
            value: replaceMappedValues(r.value),
            confidence: r.confidence,
            evidence: r.evidence ? replaceMappedValues(String(r.evidence)) : "",
          }));
        if (rows.length > 0) {
          const cols = fieldValueColumns(rows);
          blocks.push({
            content: (
              <>
                <h6 className="headings">{humanizeKey(key)}</h6>
                <InsightDataTable rows={rows} columns={cols} />
              </>
            ),
            // Two-column (no evidence) tables are narrow → allow side-by-side.
            full: cols.length !== 2,
          });
        }
        return;
      }

      // scalar (executive_summary, notes, ...)
      topScalars.push({ field: humanizeKey(key), value: String(value) });
    });

    const scalarRows = topScalars
      .filter((r) => isPdf || !isSuppressedScalar(r.value))
      .map((r) => ({ field: r.field, value: replaceMappedValues(r.value) }));

    const orderedBlocks = [];
    if (scalarRows.length > 0) {
      orderedBlocks.push({
        content: (
          <InsightDataTable
            rows={scalarRows}
            columns={fieldValueColumns(scalarRows)}
          />
        ),
        full: true, // summary/scalars can hold long text → full width
      });
    }
    orderedBlocks.push(...blocks);
    if (orderedBlocks.length === 0) return null;

    // Responsive 2-up: flex-wrap with a min cell width means half-width blocks
    // pair up on wide screens and automatically stack when the container is
    // narrow — no media query needed. The PDF stacks everything (full width) to
    // keep its captured layout predictable.
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
        {orderedBlocks.map((b, i) => (
          <div
            key={i}
            style={{
              flex: b.full || isPdf ? "1 1 100%" : "1 1 calc(50% - 8px)",
              minWidth: b.full || isPdf ? undefined : "300px",
            }}
          >
            {b.content}
          </div>
        ))}
      </div>
    );
  };

  // ─── Call Screen Integrity & Communication Flow ───
  // This section is a set of separate prompt rows (one subheading + a plain-text
  // finding each), so it renders as one Aspect | Finding table. The absorbed
  // Call Category / Agent Performance summaries (injected, no L2_promptId) are
  // kept as blocks above the table.
  const renderCallScreenIntegrity = (secs) => {
    const list = secs || [];
    const injected = list.filter(
      (s) => s && !s.L2_promptId && s.PromptResponse,
    );
    const promptRows = list
      .filter((s) => s && s.L2_promptId)
      .map((s) => ({
        aspect: replaceMappedValues(s.subHeading || ""),
        // PromptResponse for these rows is already value-mapped during grouping.
        finding:
          typeof s.PromptResponse === "string"
            ? s.PromptResponse
            : String(s.PromptResponse ?? ""),
      }))
      .filter((r) => isPdf || !isSuppressedScalar(r.finding));

    const columns = [
      {
        header: "Aspect",
        accessor: "aspect",
        cellStyle: CELL.field,
        width: "34%",
      },
      { header: "Finding", accessor: "finding", width: "66%" },
    ];

    return (
      <>
        {injected.map((s, i) => (
          <div key={`inj-${i}`} style={{ marginBottom: "12px" }}>
            <h6 className="headings">
              {replaceMappedValues(s.subHeading || "")}
            </h6>
            {typeof s.PromptResponse === "string" ? (
              <div dangerouslySetInnerHTML={{ __html: s.PromptResponse }} />
            ) : (
              s.PromptResponse
            )}
          </div>
        ))}
        {promptRows.length > 0 && (
          <InsightDataTable rows={promptRows} columns={columns} />
        )}
      </>
    );
  };

  // ─── Global Core Measures ───
  // Payload is { global_core_measures: [ { id, measure, score, evidence,
  // rationale, confidence }, ... ] }. Renders as one
  // Measure | Score | Confidence | Evidence | Rationale table.
  const renderGlobalCoreMeasures = (promptResponseJson) => {
    let parsed;
    try {
      parsed =
        typeof promptResponseJson === "string"
          ? JSON.parse(promptResponseJson)
          : promptResponseJson;
    } catch {
      return null;
    }
    const measures =
      parsed && Array.isArray(parsed.global_core_measures)
        ? parsed.global_core_measures
        : null;
    if (!measures || measures.length === 0) return null;

    const rows = measures.map((m) => ({
      measure: replaceMappedValues(
        `${m.id ? `${m.id}: ` : ""}${m.measure || ""}`,
      ),
      score: m.score !== undefined && m.score !== null ? String(m.score) : "",
      confidence: confidenceLabel(m.confidence),
      evidence: replaceMappedValues(m.evidence || ""),
      rationale: replaceMappedValues(m.rationale || ""),
    }));

    const columns = [
      {
        header: "Measure",
        accessor: "measure",
        cellStyle: CELL.field,
        width: "20%",
      },
      { header: "Score", accessor: "score", cellStyle: CELL.meta, width: "8%" },
      {
        header: "Confidence",
        accessor: "confidence",
        cellStyle: CELL.meta,
        width: "10%",
      },
      {
        header: "Evidence",
        accessor: "evidence",
        cellStyle: CELL.evidence,
        width: "31%",
      },
      { header: "Rationale", accessor: "rationale", width: "31%" },
    ];

    return <InsightDataTable rows={rows} columns={columns} />;
  };

  const toggleSection = (index) => {
    if (index === 0) return; // prevent closing first section

    setExpandedSections((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const expandAll = () => {
    setExpandedSections(sortedSections.map((_, idx) => idx));
    setActiveControl("expand");
  };

  const collapseAll = () => {
    setExpandedSections([0]);
    setActiveControl("collapse");
  };

  const formatLabel = (value) => {
    if (typeof value !== "string") return "";
    return value
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Function to insert prompt response (preserved from old code)
  function insertPromptResponse(groupSection, heading, index, newObj) {
    if (
      groupSection[heading] &&
      Array.isArray(groupSection[heading].PromptResponses)
    ) {
      groupSection[heading].PromptResponses.splice(index, 0, newObj);
    }
  }

  // ✅ PRIMARY & SECONDARY CATEGORY LOGIC (RESTORED FROM OLD CODE)
  const promptResponses = section?.items || [];

  // Get filtered call details for primary/secondary categories
  let filteredCallDetails = [];
  if (memberDetails?.callDetails && callId) {
    filteredCallDetails = memberDetails.callDetails.filter(
      (item) => item.CallId === callId,
    );
  }

  // Initialize with values from callDetails if available
  let primaryCategory =
    filteredCallDetails?.[0]?.ClientPrimaryCategory ||
    filteredCallDetails?.[0]?.callCategory ||
    "Not Provided";
  let primarySummary = "";
  let secondaryCategory =
    filteredCallDetails?.[0]?.ClientSecondaryCategory ||
    filteredCallDetails?.[0]?.secondaryCategory ||
    "N/A";
  let secondarySummary = "";

  // Member Experience placeholders
  let memberExperienceSummary = "";
  let npsCategory = "";
  let derivedReasonSummary = "";
  let communicationSummary = "";

  // Process all prompt responses to extract categories and summaries
  promptResponses.forEach((item) => {
    if (!item || !item.PromptResponse) return;
    let response = item.PromptResponse;

    try {
      // Case 1: JSON string -> parse and inspect
      if (typeof response === "string" && response.trim().startsWith("{")) {
        const parsed = JSON.parse(response);

        const parsedCategory = parsed.category || parsed.category_name || "";
        const parsedSummary = (
          parsed.summary ||
          parsed.derived_summary ||
          ""
        ).trim();

        const isPrimaryFlag =
          parsed.is_primary === true || parsed.level === "primary";
        const isSecondaryFlag =
          parsed.is_secondary === true || parsed.level === "secondary";

        // Categorization Logic
        if (parsedCategory && parsedSummary) {
          if (isPrimaryFlag) {
            primaryCategory = parsedCategory;
            primarySummary = parsedSummary;
          } else if (isSecondaryFlag) {
            secondaryCategory = parsedCategory;
            secondarySummary = parsedSummary;
          } else {
            const primaryIsEmpty = !primarySummary || primarySummary === "N/A";
            if (primaryIsEmpty) {
              if (!primaryCategory || primaryCategory === "Not Provided")
                primaryCategory = parsedCategory;
              primarySummary = parsedSummary;
            } else if (
              parsedCategory === primaryCategory &&
              (!primarySummary || primarySummary === "N/A")
            ) {
              primarySummary = parsedSummary;
            } else {
              if (!secondarySummary || secondarySummary === "N/A") {
                secondaryCategory = parsedCategory;
                secondarySummary = parsedSummary;
              }
            }
          }
        }

        // Member Experience & NPS extraction
        if (parsed.is_experience_review && parsed.member_experience) {
          memberExperienceSummary =
            parsed.derived_summaries?.member_experience_summary ||
            memberExperienceSummary ||
            "N/A";

          const npsKey = Object.keys(parsed.member_experience).find(
            (key) => key.toLowerCase() === "nps_category",
          );
          if (npsKey) {
            npsCategory = parsed.member_experience[npsKey] || "N/A";
          }

          derivedReasonSummary =
            parsed.member_experience?.derived_NPS_rationale ||
            parsed.member_experience?.derived_reason_summary ||
            parsed.member_experience?.derivedReasonSummary ||
            derivedReasonSummary ||
            "N/A";
        }

        // Communication Summary
        if (parsed.derived_summaries?.communication_summary) {
          communicationSummary =
            parsed.derived_summaries.communication_summary.trim();
        }
      }

      // Case 2: Plain string containing category: / summary: pairs
      else if (
        typeof response === "string" &&
        response.toLowerCase().includes("category:")
      ) {
        const categoryMatch = response.match(/category:\s*([^;|\n]+)/i);
        const summaryMatch = response.match(/summary:\s*([^;|\n]+)/i);

        if (categoryMatch) {
          const foundCategory = categoryMatch[1].trim();
          const foundSummary = summaryMatch ? summaryMatch[1].trim() : "";

          if (!primarySummary || primarySummary === "N/A") {
            primaryCategory = foundCategory || primaryCategory;
            primarySummary = foundSummary || "N/A";
          } else if (!secondarySummary || secondarySummary === "N/A") {
            secondaryCategory = foundCategory;
            secondarySummary = foundSummary || "N/A";
          }
        }
      }
    } catch (err) {
      console.warn("Error parsing prompt response:", err);
    }
  });

  // Apply mapping to all text content
  const mappedPrimaryCategory = replaceMappedValues(primaryCategory);
  const mappedSecondaryCategory = replaceMappedValues(secondaryCategory);
  const mappedPrimarySummary = replaceMappedValues(primarySummary);
  const mappedSecondarySummary = replaceMappedValues(secondarySummary);
  const mappedMemberExperienceSummary = replaceMappedValues(
    memberExperienceSummary,
  );
  const mappedNpsCategory = replaceMappedValues(npsCategory);
  const mappedDerivedReasonSummary = replaceMappedValues(derivedReasonSummary);
  const mappedCommunicationSummary = replaceMappedValues(communicationSummary);

  // ✅ Construct Call Category HTML with summaries (RESTORED FROM OLD CODE)
  const categoryHTML = `
    <div>
      <div style="margin-bottom:8px;">
        <strong>Primary Category:</strong> ${mappedPrimaryCategory}<br/>
        ${
          mappedPrimarySummary
            ? `<span style="color:#555; font-size:0.9em; margin-left:12px;">${mappedPrimarySummary}</span>`
            : ""
        }
      </div>
      <div style="margin-bottom:12px;">
        <strong>Secondary Category:</strong> ${mappedSecondaryCategory}<br/>
        ${
          mappedSecondarySummary
            ? `<span style="color:#555; font-size:0.9em; margin-left:12px;">${mappedSecondarySummary}</span>`
            : ""
        }
      </div>
  `;

  const categoryMemberSummaryHTML = `<div style="margin-top:10px;">
        <div style="margin-left:12px;color:#555;line-height:1.7;">
          <strong>Member Experience Summary:</strong> ${
            mappedMemberExperienceSummary || "N/A"
          }<br/>
          <strong>NPS Category:</strong> ${mappedNpsCategory || "N/A"}<br/>
          <strong>Derived NPS Rationale:</strong> ${
            mappedDerivedReasonSummary || "N/A"
          }
        </div>
      </div>
    </div>`;

  const categoryAgentSummaryHTML = `
    <div style="margin-left:12px;color:#555;">
        <strong>Communication Summary:</strong> ${
          mappedCommunicationSummary || "N/A"
        }
      </div>`;

  // Insert Score Summary
  const scoreSummarySection = groupedSections["Call Outcome"]
    ? "Call Outcome"
    : groupedSections["Evaluation Summary"]
      ? "Evaluation Summary"
      : Object.keys(groupedSections)[0];

  const normalize = (val) => val?.trim().toLowerCase();
  const TARGET = "lead handling: community origin";

  const primaryMatch = normalize(primaryCategory)?.includes(TARGET);

  const secondaryMatch = secondaryCategory
    ? secondaryCategory
        .split(",")
        .some((cat) => normalize(cat).includes(TARGET))
    : false;

  const showCallReviewTable = primaryMatch || secondaryMatch;
  const shouldShowCallReviewTable =
    !loading && !error && !is404 && showCallReviewTable && !!callReviewData;

  // Lets consumers (e.g. PDF export) know when this panel's async content —
  // notably the Call Review Sheet fetch that only fires for Community Origin
  // Lead Handling calls — has settled, instead of guessing with a fixed delay.
  useEffect(() => {
    if (typeof onReadyChange !== "function") return;
    const ready = !showCallReviewTable || !loading;
    onReadyChange(ready);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCallReviewTable, loading, callReviewData, error, is404]);

  // Metadata for the Call Review Sheet's PDF/email feedback summary.
  // Vonage Call ID / Magic Health ID sourced the same way as ContactDetails.js.
  const callReviewDetail = section?.items?.[0] || {};
  // Multi-agent calls: the review PDF/email must name EVERY handling agent, not
  // just the primary (meetingPerson). Derive all names from AgentNameJson the
  // same way ContactDetails.js does, and fall back to meetingPerson when the
  // call has a single agent / no handling-agents metadata.
  const callReviewAgentName = (() => {
    const agentJson = callReviewDetail?.AgentNameJson;
    if (agentJson) {
      try {
        const map =
          typeof agentJson === "string" ? JSON.parse(agentJson) : agentJson;
        const names = Object.values(map).filter(
          (n) =>
            n &&
            typeof n === "string" &&
            n.trim() !== "" &&
            n.toLowerCase() !== "monte nido",
        );
        if (names.length) return names.join(", ");
      } catch {
        /* fall through to meetingPerson */
      }
    }
    return replaceMappedValues(callReviewDetail.meetingPerson);
  })();
  const callReviewMemberName =
    `${memberDetails?.FirstName || ""} ${memberDetails?.LastName || ""}`.trim();
  const callReviewDate = formatDateTimeEST(section?.meetingDate);
  let callReviewMetadata = {};
  try {
    callReviewMetadata = JSON.parse(callReviewDetail?.Metadata || "{}");
  } catch {
    callReviewMetadata = {};
  }
  const callReviewVonageCallId = callReviewMetadata?.CallID;
  const callReviewMagicHealthId = memberDetails?.CustomerID;

  // Lifts the raw Call Review data (once loaded) up to the PDF exporter so
  // it can render the scoring tables natively with jsPDF-AutoTable instead
  // of screenshotting the DOM table — see InsightPDFDownload.js.
  useEffect(() => {
    if (typeof onCallReviewData !== "function") return;
    if (!showCallReviewTable || !callReviewData) {
      onCallReviewData(null);
      return;
    }
    onCallReviewData({
      callReviewData,
      agentName: callReviewAgentName,
      memberName: callReviewMemberName,
      reviewDate: callReviewDate,
      vonageCallId: callReviewVonageCallId,
      magicHealthId: callReviewMagicHealthId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCallReviewTable, callReviewData]);

  // Lift the review context so the Details page can show a feedback modal for
  // non-community (no-scoring) call categories. `isCommunityOrigin` calls keep
  // their existing in-panel Call Review flow untouched.
  useEffect(() => {
    if (typeof onReviewContext !== "function") return;
    onReviewContext({
      // The CRS fetch has settled. A 404 (is404) is the NORMAL result for
      // non-scoring call categories, so it must NOT gate readiness here —
      // otherwise the feedback button is hidden for exactly those calls.
      ready: !loading && !error,
      isCommunityOrigin: showCallReviewTable,
      // Community + CRS scoring data loaded → the scoring modal is available.
      scoringReady: shouldShowCallReviewTable,
      callId,
      agentName: callReviewAgentName,
      memberName: callReviewMemberName,
      reviewDate: callReviewDate,
      vonageCallId: callReviewVonageCallId,
      magicHealthId: callReviewMagicHealthId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loading,
    error,
    is404,
    showCallReviewTable,
    shouldShowCallReviewTable,
    callId,
    callReviewAgentName,
    callReviewMemberName,
    callReviewDate,
    callReviewVonageCallId,
    callReviewMagicHealthId,
  ]);

  if (scoreSummarySection) {
    if (!is404 && showCallReviewTable) {
      insertPromptResponse(
        groupedSections,
        "Conversation Insights - Information is power",
        1,
        {
          heading: "Conversation Insights - Information is power",
          subHeading: (
            <strong className="subheading-standout">
              Monte Nido Call Review & Scoring — Using the criteria provided by
              Monte Nido
            </strong>
          ),
          PromptResponse: loading ? (
            <p>Loading Call Review...</p>
          ) : error ? (
            <p>{error}</p>
          ) : (
            // Hidden during PDF capture (see .pdf-capture rule in
            // CallReviewTable.css) — the PDF renders these scoring tables
            // natively via jsPDF-AutoTable instead, for crisp text, correct
            // pagination and no cropped columns.
            <div className="pdf-native-table-slot">
              <CallReviewTable
                callReviewData={callReviewData}
                callId={callId}
                onRefetch={fetchCallReview}
                agentName={callReviewAgentName}
                memberName={callReviewMemberName}
                reviewDate={callReviewDate}
                vonageCallId={callReviewVonageCallId}
                magicHealthId={callReviewMagicHealthId}
                openSignal={openScoringSignal}
              />
            </div>
          ),
        },
      );
    }

    insertPromptResponse(
      groupedSections,
      "Conversation Insights - Information is power",
      2,
      {
        heading: "Conversation Insights - Information is power",
        subHeading: "Magic Health Evaluation Score Summary",
        PromptResponse: renderCoreMeasuresSummaryTable(),
      },
    );
    insertPromptResponse(
      groupedSections,
      "Conversation Insights - Information is power",
      1,
      {
        heading: "Conversation Insights - Information is power",
        subHeading: "Member Experience",
        PromptResponse: categoryMemberSummaryHTML.trim(),
      },
    );
  }

  // Insert Category / Agent Performance subsections.
  //
  // Per the simplification proposal the Purpose section is removed from the
  // page view: its "Call Category" content is absorbed into "Call Screen
  // Integrity & Communication Flow" and "Agent Performance" is dropped. The PDF
  // export keeps the original Purpose section intact, so when rendering for PDF
  // (isPdf) we inject both into "Purpose" exactly as before.
  if (isPdf) {
    insertPromptResponse(groupedSections, "Purpose", 0, {
      heading: "Purpose",
      subHeading: "Call Category",
      PromptResponse: categoryHTML.trim(),
    });

    insertPromptResponse(groupedSections, "Purpose", 3, {
      heading: "Purpose",
      subHeading: "Agent Performance",
      PromptResponse: categoryAgentSummaryHTML.trim(),
    });
  } else {
    // Page view: Call Screen Integrity & Communication Flow absorbs both the
    // Call Category and Agent Performance summaries from the removed Purpose
    // section — they lead the section, ahead of its detail rows.
    insertPromptResponse(
      groupedSections,
      "Call Screen Integrity & Communication Flow",
      0,
      {
        heading: "Call Screen Integrity & Communication Flow",
        subHeading: "Call Category",
        PromptResponse: categoryHTML.trim(),
      },
    );
    insertPromptResponse(
      groupedSections,
      "Call Screen Integrity & Communication Flow",
      1,
      {
        heading: "Call Screen Integrity & Communication Flow",
        subHeading: "Agent Performance",
        PromptResponse: categoryAgentSummaryHTML.trim(),
      },
    );
  }

  const renderValue = (
    val,
    level = 0,
    isFirstInSection = false,
    shouldFilterSummary = false,
  ) => {
    if (val === null || val === undefined) return "N/A";
    if (typeof val === "boolean") return val.toString();

    // Check if the value is a string that might be a stringified JSON
    if (typeof val === "string" && val.trim().startsWith("{")) {
      try {
        const nestedParsed = JSON.parse(val);
        return renderValue(
          nestedParsed,
          level + 1,
          isFirstInSection,
          shouldFilterSummary,
        );
      } catch (e) {
        return replaceMappedValues(val);
      }
    }

    if (Array.isArray(val)) {
      return (
        <div style={{ paddingLeft: `${level + 1}rem`, marginTop: "4px" }}>
          {val.map((item, idx) => (
            <div
              key={idx}
              style={{
                borderLeft: "2px solid #ddd",
                paddingLeft: "0.75rem",
                marginBottom: "4px",
              }}
            >
              {typeof item === "object"
                ? renderValue(
                    item,
                    level + 1,
                    isFirstInSection && idx === 0,
                    shouldFilterSummary,
                  )
                : replaceMappedValues(formatLabel(item?.toString()))}
            </div>
          ))}
        </div>
      );
    }

    if (typeof val === "object") {
      const getDetailAsString = (detail) => {
        if (!detail) return "";

        if (typeof detail === "string") {
          try {
            const parsed = JSON.parse(detail);
            if (parsed.evidence_text) {
              let evidenceText = parsed.evidence_text;
              evidenceText = evidenceText.replace(/\\"/g, '"');
              return evidenceText;
            }
            return JSON.stringify(parsed, null, 2);
          } catch (e) {
            return detail;
          }
        }

        if (typeof detail === "object") {
          try {
            if (detail.evidence_text) {
              let evidenceText = detail.evidence_text;
              if (typeof evidenceText === "string") {
                evidenceText = evidenceText.replace(/\\"/g, '"');
              }
              return evidenceText;
            }
            return JSON.stringify(detail, null, 2);
          } catch (e) {
            return String(detail);
          }
        }

        return String(detail);
      };

      const followUpFields = extractFollowUpFields(val);
      const hasFollowUpData = hasFollowUpFields(val);

      const hasEvidence = val.evidence && String(val.evidence).trim();
      const hasRationale = val.rationale && String(val.rationale).trim();

      const otherKeys = Object.keys(val).filter((k) => {
        const normalized = normalizeKey(k);
        const isFollowUp = [
          "follow_up_commitment",
          "follow_up_details",
          "follow_up_evidence",
          "follow_up_confidence_score",
          "follow_up_scheduled",
          "follow_up_date",
          "follow_up_purpose",
        ].includes(normalized);
        if (k === "evidence" || k === "rationale" || isFollowUp) return false;
        // Page view only: drop N/A fields and confidence rows lacking evidence.
        if (!isPdf) {
          if (isNaScalar(val[k])) return false;
          if (normalized.includes("confidence") && !hasEvidence) return false;
        }
        return true;
      });

      if (
        hasFollowUpData &&
        otherKeys.length === 0 &&
        !hasEvidence &&
        !hasRationale
      ) {
        return (
          <CollapsibleFollowUp
            commitment={replaceMappedValues(
              getDetailAsString(followUpFields.commitment),
            )}
            details={replaceMappedValues(
              getDetailAsString(followUpFields.details),
            )}
            evidence={replaceMappedValues(
              getDetailAsString(followUpFields.evidence),
            )}
            confidence={replaceMappedValues(
              getDetailAsString(followUpFields.confidence),
            )}
            scheduled={replaceMappedValues(
              getDetailAsString(followUpFields.scheduled),
            )}
            date={replaceMappedValues(getDetailAsString(followUpFields.date))}
            purpose={replaceMappedValues(
              getDetailAsString(followUpFields.purpose),
            )}
          />
        );
      }

      if (
        hasFollowUpData &&
        (otherKeys.length > 0 || hasEvidence || hasRationale)
      ) {
        return (
          <div
            style={{
              paddingLeft: `${level + 1}rem`,
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              marginTop: "6px",
            }}
          >
            <CollapsibleFollowUp
              commitment={replaceMappedValues(
                getDetailAsString(followUpFields.commitment),
              )}
              details={replaceMappedValues(
                getDetailAsString(followUpFields.details),
              )}
              evidence={replaceMappedValues(
                getDetailAsString(followUpFields.evidence),
              )}
              confidence={replaceMappedValues(
                getDetailAsString(followUpFields.confidence),
              )}
              scheduled={replaceMappedValues(
                getDetailAsString(followUpFields.scheduled),
              )}
              date={replaceMappedValues(getDetailAsString(followUpFields.date))}
              purpose={replaceMappedValues(
                getDetailAsString(followUpFields.purpose),
              )}
            />

            {otherKeys.map((subKey, idx) => {
              const subVal = val[subKey];
              const formattedVal = formatConfidenceValue(subKey, subVal);
              return (
                <div key={subKey} style={{ lineHeight: "1.4em" }}>
                  <span style={{ fontWeight: 485 }}>{formatLabel(subKey)}</span>{" "}
                  -{" "}
                  {typeof subVal === "object" ? (
                    renderValue(subVal, level + 1, false, shouldFilterSummary)
                  ) : (
                    <span style={{ fontWeight: 400 }}>
                      {replaceMappedValues(
                        formatLabel(formattedVal?.toString()),
                      )}
                    </span>
                  )}
                </div>
              );
            })}

            {(hasEvidence || hasRationale) && (
              <CollapsibleEvidenceRationale
                evidence={replaceMappedValues(getDetailAsString(val.evidence))}
                rationale={replaceMappedValues(
                  getDetailAsString(val.rationale),
                )}
              />
            )}
          </div>
        );
      }

      if ((hasEvidence || hasRationale) && otherKeys.length === 0) {
        return (
          <CollapsibleEvidenceRationale
            evidence={replaceMappedValues(getDetailAsString(val.evidence))}
            rationale={replaceMappedValues(getDetailAsString(val.rationale))}
          />
        );
      }

      if (hasEvidence || hasRationale) {
        return (
          <div
            style={{
              paddingLeft: `${level + 1}rem`,
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              marginTop: "6px",
            }}
          >
            {otherKeys.map((subKey, idx) => {
              const subVal = val[subKey];
              const formattedVal = formatConfidenceValue(subKey, subVal);
              return (
                <div key={subKey} style={{ lineHeight: "1.4em" }}>
                  <span style={{ fontWeight: 485 }}>{formatLabel(subKey)}</span>{" "}
                  -{" "}
                  {typeof subVal === "object" ? (
                    renderValue(subVal, level + 1, false, shouldFilterSummary)
                  ) : (
                    <span style={{ fontWeight: 400 }}>
                      {replaceMappedValues(
                        formatLabel(formattedVal?.toString()),
                      )}
                    </span>
                  )}
                </div>
              );
            })}
            <CollapsibleEvidenceRationale
              evidence={replaceMappedValues(getDetailAsString(val.evidence))}
              rationale={replaceMappedValues(getDetailAsString(val.rationale))}
            />
          </div>
        );
      }

      // Filter out summary fields, evidence/rationale, and follow-up fields if we're in the main content area
      const entries = Object.entries(val).filter(([key, value]) => {
        if (shouldFilterSummary && key.endsWith("_summary")) {
          return false; // Filter out summary fields
        }
        if (key === "evidence" || key === "rationale") {
          return false;
        }
        const normalized = normalizeKey(key);
        if (
          [
            "follow_up_commitment",
            "follow_up_details",
            "follow_up_evidence",
            "follow_up_confidence_score",
            "follow_up_scheduled",
            "follow_up_date",
            "follow_up_purpose",
          ].includes(normalized)
        ) {
          return false;
        }
        // Page view only: drop N/A fields and confidence rows lacking evidence.
        if (!isPdf) {
          if (isNaScalar(value)) return false;
          if (normalized.includes("confidence") && !hasEvidence) return false;
        }
        return true;
      });

      if (entries.length === 0) {
        return null; // Don't render anything if all entries were filtered out
      }
      return (
        <div
          style={{
            paddingLeft: `${level + 1}rem`,
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            marginTop: "6px",
          }}
        >
          {entries.map(([subKey, subVal], idx) => {
            // Special handling for confidence fields
            if (subKey.toLowerCase().includes("confidence")) {
              const confidenceValue = Number(subVal);

              let confidenceText = subVal;

              switch (confidenceValue) {
                case 1:
                  confidenceText = "Low";
                  break;
                case 3:
                  confidenceText = "Medium";
                  break;
                case 5:
                  confidenceText = "High";
                  break;
                default:
                  confidenceText = subVal;
              }

              return (
                <div key={subKey} style={{ lineHeight: "1.4em" }}>
                  <span style={{ fontWeight: 485 }}>{formatLabel(subKey)}</span>{" "}
                  - {confidenceText}
                </div>
              );
            }

            // Check if this is a summary field (ends with _summary)
            const isSummary = subKey.endsWith("_summary");

            // Format the key name for display
            let displayKey = formatLabel(subKey);

            // If it's the first item in the section and it's a summary, make it bold
            const isFirstAndSummary =
              isFirstInSection && idx === 0 && isSummary;

            return (
              <div key={subKey} style={{ lineHeight: "1.4em" }}>
                <span
                  style={{
                    fontWeight: isFirstAndSummary ? 600 : 485,
                    fontSize: isFirstAndSummary ? "1.1em" : "1em",
                  }}
                >
                  {displayKey}
                </span>{" "}
                -{" "}
                {typeof subVal === "object" ? (
                  renderValue(subVal, level + 1, false, shouldFilterSummary)
                ) : (
                  <span
                    style={{
                      fontWeight: isFirstAndSummary ? 500 : 400,
                    }}
                  >
                    {replaceMappedValues(formatLabel(String(subVal ?? "")))}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    return typeof val === "string" ? replaceMappedValues(val) : val;
  };

  const firstCallDetail = section?.items?.[0] || {};
  // console.log(callTimings);

  return (
    <div className="insight-panel" style={style}>
      <div className="insight-header">
        <div style={{ flex: 1 }}>
          <h2>
            Pathway Driven Insight For {memberDetails?.FirstName}{" "}
            {memberDetails?.LastName} -{" "}
            {replaceMappedValues(firstCallDetail.meetingPerson)} for Date:{" "}
            {formatDateTimeEST(section?.meetingDate)}
          </h2>
        </div>

        <div className="header-right-container">
          {!forceExpand && (
            <div className="panel-controls">
              <span
                className={`control-icon ${
                  activeControl === "expand" ? "active" : ""
                }`}
                onClick={expandAll}
              >
                <IoIosArrowDropdown
                  style={{ fontSize: 20, marginRight: "4px" }}
                />{" "}
                Expand All
              </span>
              <span
                className={`control-icon1 ${
                  activeControl === "collapse" ? "active" : ""
                }`}
                onClick={collapseAll}
              >
                <IoIosArrowDropup
                  style={{ fontSize: 20, marginRight: "4px" }}
                />{" "}
                Collapse All
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="insight-body">
        {sortedSections.map((group, index) => {
          // Check if this section is for Agent Scoring (case-insensitive)
          const isAgentScoringSection = group.heading
            ?.toLowerCase()
            .includes("agent scoring");

          // Sections opted into the reusable column-based table layout.
          const isTableSection = TABLE_SECTION_HEADINGS.has(group.heading);

          // Follow Up Insights uses its own adapter (single task record).
          const isFollowUpSection = group.heading === FOLLOW_UP_HEADING;

          // Category sections rendered as grouped sub-tables (Lead Intake, ...).
          const isGroupedSection = GROUPED_TABLE_HEADINGS.has(group.heading);

          // Call Screen Integrity aggregates its prompts into one table.
          const isCallScreenSection = group.heading === CALL_SCREEN_HEADING;

          // Sections with their own renderer produce nothing when the pipeline
          // gave them nothing to show — a Follow Up Detail of "{}", say. Render
          // once and drop the whole section, so the heading never appears above
          // an empty body.
          const hasCustomRenderer = isCallScreenSection || isFollowUpSection;
          const customContent = isCallScreenSection
            ? renderCallScreenIntegrity(group.PromptResponses)
            : isFollowUpSection
              ? renderFollowUpSection(group.PromptResponses)
              : null;

          if (
            hasCustomRenderer &&
            !customContent &&
            !sectionSummaries[group.heading]
          ) {
            return null;
          }

          // Global Core Measures renders its measures array as a table.
          const isGlobalCoreMeasuresSection =
            group.heading === GLOBAL_CORE_MEASURES_HEADING;

          // The Purpose section is removed from the page view (its Call Category
          // / Agent Performance summaries are absorbed into "Call Screen
          // Integrity & Communication Flow"). It only reappears here because the
          // raw prompt data still carries a Purpose section, so skip it on the
          // page. The PDF (forceExpand) keeps Purpose intact.
          if (!forceExpand && group.heading === "Purpose") {
            return null;
          }

          return (
            <div key={index} className="section">
              <div
                className={`section-header ${
                  index < expandSectionsIndex ? "fixed" : ""
                }`}
                onClick={() =>
                  index !== 0 && !forceExpand && toggleSection(index)
                }
              >
                <h3>{group.heading}</h3>
                {index !== 0 && (
                  <span className="arrow">
                    {expandedSections.includes(index) ? (
                      <img src={arrowDown} alt="v" />
                    ) : (
                      <img src={arrowUp} alt="^" />
                    )}
                  </span>
                )}
              </div>
              {expandedSections.includes(index) && (
                <div className="section-content">
                  {/* Display section summary first if it exists */}
                  {sectionSummaries[group.heading] && (
                    <div
                      style={{
                        marginBottom: "20px",
                        padding: "12px",
                        backgroundColor: "#f8f9fa",
                        borderLeft: "4px solid #007bff",
                        borderRadius: "4px",
                      }}
                    >
                      <strong
                        style={{
                          fontSize: "1.1em",
                          display: "block",
                          marginBottom: "8px",
                        }}
                      >
                        {sectionSummaries[group.heading].displayLabel}
                      </strong>
                      <div style={{ fontWeight: 500 }}>
                        {replaceMappedValues(
                          sectionSummaries[group.heading].value,
                        )}
                      </div>
                    </div>
                  )}

                  {/* Then display all prompt responses */}
                  {hasCustomRenderer
                    ? customContent
                    : group.PromptResponses.map((sec, idx) => {
                          const renderContent = () => {
                            if (
                              isAgentScoringSection &&
                              typeof sec.PromptResponse === "string" &&
                              sec.PromptResponse.trim().startsWith("{")
                            ) {
                              console.log(
                                "Rendering Agent Scoring with filtered metrics",
                              );
                              return renderAgentScoringMetrics(
                                sec.PromptResponse,
                              );
                            }

                            if (
                              isGroupedSection &&
                              typeof sec.PromptResponse === "string" &&
                              sec.PromptResponse.trim().startsWith("{")
                            ) {
                              const grouped = renderGroupedSection(
                                sec.PromptResponse,
                              );
                              if (grouped) return grouped;
                              // fall through to the generic renderer if empty
                            }

                            if (
                              isGlobalCoreMeasuresSection &&
                              typeof sec.PromptResponse === "string" &&
                              sec.PromptResponse.trim().startsWith("{")
                            ) {
                              const gcm = renderGlobalCoreMeasures(
                                sec.PromptResponse,
                              );
                              if (gcm) return gcm;
                              // fall through to the generic renderer if empty
                            }

                            if (
                              isTableSection &&
                              typeof sec.PromptResponse === "string" &&
                              sec.PromptResponse.trim().startsWith("{")
                            ) {
                              const dataTable = renderDataTable(
                                sec.PromptResponse,
                              );
                              if (dataTable) return dataTable;
                              // fall through to the generic renderer if empty
                            }

                            if (
                              typeof sec.PromptResponse === "string" &&
                              sec.PromptResponse.trim().startsWith("{")
                            ) {
                              try {
                                const parsed = JSON.parse(sec.PromptResponse);
                                return renderValue(parsed, 0, idx === 0, true);
                              } catch (e) {
                                return (
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: sec.PromptResponse,
                                    }}
                                  />
                                );
                              }
                            } else if (typeof sec.PromptResponse === "string") {
                              const cleanedText = cleanBeginningMiddleEnd(
                                sec.PromptResponse,
                                sec.L1_promptId,
                              );
                              return (
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: replaceMappedValues(cleanedText),
                                  }}
                                />
                              );
                            } else if (
                              React.isValidElement(sec.PromptResponse)
                            ) {
                              return sec.PromptResponse;
                            } else {
                              return (
                                <div>
                                  {replaceMappedValues(
                                    String(sec.PromptResponse || "N/A"),
                                  )}
                                </div>
                              );
                            }
                          };

                          if (
                            sec.subHeading ===
                            "Magic Health Evaluation Score Summary"
                          ) {
                            return (
                              <div key={idx} className="section">
                                <div
                                  className="section-header"
                                  onClick={() =>
                                    setIsEvalScoreExpanded((prev) => !prev)
                                  }
                                >
                                  <h3>
                                    {replaceMappedValues(
                                      sec.subHeading || "Subheading",
                                    )}
                                  </h3>
                                  <span className="arrow">
                                    {isEvalScoreExpanded ? (
                                      <img src={arrowDown} alt="v" />
                                    ) : (
                                      <img src={arrowUp} alt="^" />
                                    )}
                                  </span>
                                </div>
                                {isEvalScoreExpanded && (
                                  <div className="section-content">
                                    {renderContent()}
                                  </div>
                                )}
                              </div>
                            );
                          }

                          return (
                            <div key={idx} style={{ marginBottom: "15px" }}>
                              <h6 className="headings">
                                {replaceMappedValues(
                                  sec.subHeading || "Subheading",
                                )}
                              </h6>
                              {renderContent()}
                            </div>
                          );
                        })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InsightPanel;
